import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { useApp } from "../context/AppContext";
import { isAppleSignInAvailable, signInWithApple } from "../utils/appleSignIn";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const googleAuthUrl = `${API}/api/auth/google${Capacitor.isNativePlatform() ? "?client=app" : ""}`;

const SocialAuthButtons = ({ onError, onLoadingChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useApp();
  const [appleLoading, setAppleLoading] = useState(false);
  const showApple = isAppleSignInAvailable();

  const setLoading = (value) => {
    setAppleLoading(value);
    if (typeof onLoadingChange === "function") onLoadingChange(value);
  };

  // Guideline 4: never send the user to the external browser to sign in. On native
  // (iOS/Android) open the Google OAuth flow inside the app via SFSafariViewController
  // (iOS) / Chrome Custom Tabs (Android) using @capacitor/browser. The backend then
  // redirects to taweelai://callback?token=..., which the deep-link handler in
  // capacitorBridge.js catches (and closes this in-app browser).
  const handleGoogleSignIn = async (e) => {
    if (!Capacitor.isNativePlatform()) return; // web: let the <a href> navigate normally
    e.preventDefault();
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: googleAuthUrl, presentationStyle: "popover" });
    } catch (err) {
      if (typeof onError === "function") onError({ type: "error", msg: err.message || t("oauthFailed") });
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    try {
      const data = await signInWithApple();
      if (data?.token) localStorage.setItem("auth_token", data.token);
      login(data.user);
      const isNewUser = Date.now() - new Date(data.user.createdAt).getTime() < 1e4;
      navigate(isNewUser ? "/welcome" : "/chat");
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t("oauthFailed");
      if (typeof onError === "function") onError({ type: "error", msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="oauth-row">
      {showApple && (
        <button
          type="button"
          className="oauth-btn oauth-btn--apple"
          onClick={handleAppleSignIn}
          disabled={appleLoading}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              fill="currentColor"
              d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
            />
          </svg>
          {appleLoading ? t("signingIn") : t("continueWithApple")}
        </button>
      )}
      <a href={googleAuthUrl} className="oauth-btn" onClick={handleGoogleSignIn}>
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {t("continueWithGoogle")}
      </a>
    </div>
  );
};

export default SocialAuthButtons;
